import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button, Radio } from '@/components/ui'
import { api } from '@/lib/api'
import type { Invoice, PaginatedResponse } from '@/types'
import type { PaymentListItem } from '@/hooks/usePayments'
import { fetchAllInvoices, fetchAllPayments } from './exportUtils'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useSelectedPropertyStore } from '@/stores'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  dataType: 'invoices' | 'payments' | 'customers'
  dateFrom?: string
  dateTo?: string
  status?: string
}

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function escapeCsv(value: unknown): string {
  const str = String(value ?? '')
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

// MON-18 B: both CSVs carry a Building column (the property the invoice or
// payment's unit belongs to). Headers stay English like the other columns.
function invoicesToCsv(invoices: Invoice[]): string {
  const headers = ['Number', 'Customer', 'Unit', 'Building', 'Billed Date', 'Due Date', 'Total', 'Amount Paid', 'Balance Due', 'Status']
  const rows = invoices.map((inv) => [
    escapeCsv(inv.number),
    escapeCsv(inv.customer?.name),
    escapeCsv(inv.unit?.name),
    escapeCsv(inv.property?.name),
    escapeCsv(inv.billed_at),
    escapeCsv(inv.due_at),
    escapeCsv(inv.total),
    escapeCsv(inv.amount_paid),
    escapeCsv(inv.balance_due),
    escapeCsv(inv.paid ? 'Paid' : inv.overdue ? 'Overdue' : 'Pending'),
  ])
  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
}

function paymentsToCsv(payments: PaymentListItem[]): string {
  const headers = ['Date', 'Invoice', 'Customer', 'Unit', 'Building', 'Amount', 'Method', 'Reference']
  const rows = payments.map((p) => [
    escapeCsv(p.paid_at),
    escapeCsv(p.invoice_number),
    escapeCsv(p.customer?.name),
    escapeCsv(p.unit?.name),
    escapeCsv(p.property?.name),
    escapeCsv(p.amount),
    escapeCsv(p.payment_method),
    escapeCsv(p.reference),
  ])
  return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
}

export function ExportModal({ isOpen, onClose, dataType, dateFrom, dateTo, status }: ExportModalProps) {
  const { t } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  // MON-18 B: the export follows the header's selected building; no
  // selection exports every building (unchanged from before).
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const { timezone } = useLocalizationConfig()

  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async () => {
    setIsExporting(true)
    setError(null)
    try {
      let csv: string
      let filename: string
      let rowCount = 0
      // DAT-1: the filename's date is the account's local "today"
      // (localToday), not the UTC calendar day: the old
      // toISOString().slice(0,10) named a file "yesterday" during the
      // account's early morning hours (e.g. Asia/Bangkok 00:00-07:00).
      const dateStr = localToday(timezone)

      if (dataType === 'invoices') {
        const invoices = await fetchAllInvoices(dateFrom, dateTo, status, undefined, selectedPropertyId ?? undefined)
        csv = invoicesToCsv(invoices)
        rowCount = invoices.length
        filename = `invoices-${dateStr}.csv`
      } else if (dataType === 'payments') {
        const payments = await fetchAllPayments(dateFrom, dateTo, undefined, selectedPropertyId ?? undefined)
        csv = paymentsToCsv(payments)
        rowCount = payments.length
        filename = `payments-${dateStr}.csv`
      } else {
        // customers — fetch all pages
        const all: Record<string, unknown>[] = []
        let page = 1
        let hasMore = true
        while (hasMore) {
          const { data } = await api.get<PaginatedResponse<Record<string, unknown>[]>>('/api/customers', { params: { page, per_page: 100 } })
          all.push(...data.data)
          hasMore = data.meta.current_page < data.meta.total_pages
          page++
        }
        const headers = ['Name', 'Phone', 'Email', 'ID Card', 'LINE ID', 'Status']
        const rows = all.map((c) => [
          escapeCsv(c.name),
          escapeCsv(c.phone),
          escapeCsv(c.email),
          escapeCsv(c.id_card_number),
          escapeCsv(c.line_id),
          escapeCsv(c.status),
        ])
        csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
        rowCount = all.length
        filename = `customers-${dateStr}.csv`
      }

      downloadBlob(csv, filename, 'text/csv;charset=utf-8;')
      addToast({ type: 'success', message: t('export_data.success', { count: rowCount }) })
      // Auto-close after short delay to let browser download start
      setTimeout(() => handleClose(), 300)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setIsExporting(false)
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('export_data.title')}>
      <div className="space-y-4">
        {/* Format */}
        <div>
          <label className="label">
            <span className="label-text font-medium">{t('export_data.format')}</span>
          </label>
          <div className="flex gap-3">
            <Radio
              name="format"
              variant="primary"
              size="sm"
              checked
              readOnly
              label={t('export_data.csv')}
            />
          </div>
        </div>

        {isExporting && (
          <div className="flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-sm">{t('export_data.processing')}</span>
          </div>
        )}
        {error && (
          <div className="text-error text-sm">
            {t('export_data.failed')}: {error}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 mt-6">
        <Button variant="ghost" onClick={handleClose}>
          {t('actions.close')}
        </Button>
        <Button
          onClick={handleExport}
          loading={isExporting}
          leftIcon={<Download className="w-4 h-4" />}
        >
          {t('export_data.export_button')}
        </Button>
      </div>
    </Modal>
  )
}
